import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, X, ChevronLeft, ChevronRight as ChevR, ArrowRight, Phone } from "lucide-react";
import { GALLERY as FALLBACK_GALLERY, GALLERY_CATS as FALLBACK_CATS, COMPANY, ASSETS } from "../mock";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";
import { BeforeAfterSection } from "../components/BeforeAfterSlider";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveSrc = (s) => (s && s.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s}` : s);

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(-1);
  const [items, setItems] = useState(FALLBACK_GALLERY.map((g) => ({ ...g, src: g.src })));
  const [cats, setCats] = useState(FALLBACK_CATS);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    axios.get(`${API}/gallery`).then((res) => {
      if (!active || !res.data?.items?.length) return;
      setItems(res.data.items.map((g) => ({ ...g, src: resolveSrc(g.src) })));
      setCats(["All", ...(res.data.categories || [])]);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const filtered = useMemo(
    () => (cat === "All" ? items : items.filter((g) => g.cat === cat)),
    [cat, items]
  );

  useScrollReveal([cat]);
  useSEO({
    title: "Screen Enclosure & Pool Cage Gallery in Melbourne, FL | Miller & Miller",
    description: "Browse our gallery of custom pool enclosures, patio screens, rescreens, carports & more built across the Space Coast by Miller & Miller Services LLC.",
    keywords: ["pool enclosure photos", "screen enclosure gallery", "pool cage examples Melbourne FL"],
    path: "/gallery",
  });

  const close = useCallback(() => setLightbox(-1), []);
  const prev = useCallback(() => setLightbox((i) => (i <= 0 ? filtered.length - 1 : i - 1)), [filtered.length]);
  const next = useCallback(() => setLightbox((i) => (i >= filtered.length - 1 ? 0 : i + 1)), [filtered.length]);

  useEffect(() => {
    if (lightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, prev, next]);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-24 bg-brand-navy text-white overflow-hidden">
        <img src={ASSETS.pool1} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/90 via-brand-navy/80 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">Gallery</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold">Our Work Speaks For Itself</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">
            Explore a selection of screen enclosures, pool cages, and outdoor upgrades we've crafted across the Space Coast.
          </p>
        </div>
      </section>

      <BeforeAfterSection />

      {/* Filters + grid */}
      <section className="section-y bg-white">
        <div className="container-x">
          <div className="flex flex-wrap gap-2.5 justify-center mb-10">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm font-heading font-bold transition-colors ${
                  cat === c
                    ? "bg-brand-ocean text-white shadow-glow"
                    : "bg-brand-mist text-brand-navy hover:bg-brand-sky/20"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
            {filtered.map((g, i) => (
              <button
                key={`${g.src}-${i}`}
                onClick={() => setLightbox(i)}
                className="reveal group mb-4 block w-full overflow-hidden rounded-xl shadow-card ring-1 ring-brand-mist break-inside-avoid text-left"
                style={{ transitionDelay: `${(i % 4) * 50}ms` }}
              >
                <div className="relative">
                  <img src={g.src} alt={g.title} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 inset-x-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                    <span className="text-brand-gold2 text-[11px] font-heading font-bold uppercase tracking-wider">{g.cat}</span>
                    <div className="text-white font-heading font-bold">{g.title}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-brand-ocean to-brand-sky">
        <div className="container-x py-14 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-extrabold text-white">Love what you see?</h2>
          <p className="text-white/90 mt-3">Let's design a backyard retreat you'll love for years to come.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button onClick={() => navigate("/estimate")} className="h-12 px-8 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-brand-navy/30 hover:bg-brand-navy/50 text-white font-heading font-bold ring-1 ring-white/30 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox >= 0 && filtered[lightbox] && (
        <div className="fixed inset-0 z-[1000] bg-black/90 grid place-items-center p-4" onClick={close}>
          <button onClick={close} aria-label="Close" className="absolute top-5 right-5 h-11 w-11 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25">
            <X className="h-6 w-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous" className="absolute left-3 md:left-6 h-12 w-12 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25">
            <ChevronLeft className="h-7 w-7" />
          </button>
          <figure className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img src={filtered[lightbox].src} alt={filtered[lightbox].title} className="max-h-[78vh] w-auto mx-auto rounded-lg shadow-2xl object-contain" />
            <figcaption className="text-center mt-4 text-white">
              <span className="text-brand-gold2 text-xs font-heading font-bold uppercase tracking-wider">{filtered[lightbox].cat}</span>
              <div className="font-heading font-bold text-lg">{filtered[lightbox].title}</div>
            </figcaption>
          </figure>
          <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next" className="absolute right-3 md:right-6 h-12 w-12 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25">
            <ChevR className="h-7 w-7" />
          </button>
        </div>
      )}
    </div>
  );
}
